// Recognition mode inside the conversation composer.
//
// @recognition is a TRANSIENT composer mode, not mention syntax. Nothing about
// it is persisted into the message body or into mention_user_ids: picking it
// strips the "@…" the user typed and flips a flag. Leaving the mode gives them
// their words back as an ordinary draft.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  suggestRecognition,
  type RecognitionContextMessage,
  type RecognitionSuggestion,
} from '@/services/recognitionService';

/** How long typing must settle before the classifier is asked. Long enough that
 *  a sentence costs one call, short enough that the preview feels live. */
export const SUGGEST_DEBOUNCE_MS = 700;

/** Below this there is nothing worth interpreting, and asking would just burn a
 *  model call on "th". */
export const MIN_DRAFT_CHARS = 3;

export type RecognitionPhase =
  | 'idle'
  | 'analyzing'
  | 'ready'
  | 'needs_recipient'
  | 'needs_detail'
  | 'blocked'
  | 'error';

export interface RecognitionDraftState {
  phase: RecognitionPhase;
  suggestion: RecognitionSuggestion | null;
  /** Set when the workspace cannot do this at all, so the panel can say why
   *  instead of letting someone reach Recognize and be refused. */
  blockedReason?: string;
}

/** The slice of the thread the composer is allowed to send as context. */
export interface RecognitionContext {
  conversationId: string;
  replyToMessageId?: string;
  explicitRecipientUserIds: string[];
  contextMessages: RecognitionContextMessage[];
}

/**
 * Derive the phase from a suggestion. Pure, so the state machine is testable
 * without a network or a DOM.
 *
 * Order matters: blocked beats everything (there is no point asking who), and
 * a missing recipient beats a thin description (you cannot recognise nobody,
 * however well you describe it).
 */
export function phaseFor(s: RecognitionSuggestion | null): RecognitionPhase {
  if (!s) return 'idle';
  if (s.blocked) return 'blocked';
  if (s.needs_recipient) return 'needs_recipient';
  if (s.needs_detail) return 'needs_detail';
  return 'ready';
}

/**
 * Debounced, cancellable recognition analysis for the current draft.
 *
 * Every superseded request is aborted. Without that, a slow early response can
 * land after a fast later one and overwrite the preview with analysis of text
 * the user has already changed — which reads as the AI ignoring what they typed.
 */
export function useRecognitionDraft(active: boolean, draft: string, ctx: RecognitionContext) {
  const [state, setState] = useState<RecognitionDraftState>({ phase: 'idle', suggestion: null });
  const abortRef = useRef<AbortController | null>(null);
  // The draft we last analysed. Re-sending identical text wastes a model call
  // on nothing — a cursor move or a re-render is not a change.
  const lastSentRef = useRef<string>('');

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    lastSentRef.current = '';
    setState({ phase: 'idle', suggestion: null });
  }, []);

  useEffect(() => {
    if (!active) {
      reset();
      return;
    }
    const text = draft.trim();
    if (text.length < MIN_DRAFT_CHARS) return;
    if (text === lastSentRef.current) return;

    const h = window.setTimeout(() => {
      abortRef.current?.abort(); // supersede any in-flight analysis
      const ac = new AbortController();
      abortRef.current = ac;
      lastSentRef.current = text;
      setState((prev) => ({ ...prev, phase: 'analyzing' }));

      suggestRecognition(
        {
          draft_text: text,
          conversation_id: ctx.conversationId,
          reply_to_message_id: ctx.replyToMessageId,
          explicit_recipient_user_ids: ctx.explicitRecipientUserIds,
          context_messages: ctx.contextMessages,
        },
        ac.signal,
      )
        .then((s) => {
          if (ac.signal.aborted) return;
          setState({ phase: phaseFor(s), suggestion: s, blockedReason: s.blocked_reason });
        })
        .catch((e) => {
          if (ac.signal.aborted || (e as { name?: string })?.name === 'CanceledError') return;
          // A failed analysis must never stop someone recognising a colleague;
          // the panel stays usable and they can fill it in themselves.
          setState({ phase: 'error', suggestion: null });
        });
    }, SUGGEST_DEBOUNCE_MS);

    return () => window.clearTimeout(h);
  }, [active, draft, ctx.conversationId, ctx.replyToMessageId, reset,
      // Identity-stable keys: the arrays are rebuilt each render, so depending
      // on them directly would re-run this effect forever.
      ctx.explicitRecipientUserIds.join(','), ctx.contextMessages.length]);

  // Abort on unmount, so switching conversation mid-analysis does not land a
  // response into a composer that is now showing a different thread.
  useEffect(() => () => abortRef.current?.abort(), []);

  return { ...state, reset };
}
